import CheckPermission from "../../../hooks/CheckPermission";
import React, { useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaPlus,
  FaDownload,
  FaPaperclip,
  FaPrint,
  FaSave,
  FaUser,
  FaClock,
} from "react-icons/fa";
import CustomButton from "../../../shared/CustomButton";
import { useTranslation } from "react-i18next";
// Custom CSS for line clamping
const customStyles = `
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const NotesTab = ({ patient }) => {
  const { hasPermission } = CheckPermission();
  const [noteContent, setNoteContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [reasonForVisit, setReasonForVisit] = useState("");
  const { t } = useTranslation();
  const [savedNotes, setSavedNotes] = useState([
    {
      id: 1,
      content:
        "<p>Patient presented with chest pain and shortness of breath. Initial assessment shows stable vitals. Recommended ECG and chest X-ray.</p>",
      timestamp: "2024-06-28 10:30 AM",
      username: "Dr. Sarah Johnson",
      templateName: "Initial Assessment",
      reasonForVisit: "Chest Pain",
    },
    {
      id: 2,
      content:
        "<p>Follow-up appointment. Patient reports improvement in symptoms. Pain reduced from 8/10 to 3/10. Continue current medication regimen.</p>",
      timestamp: "2024-06-27 2:15 PM",
      username: "Dr. Michael Chen",
      templateName: null,
      reasonForVisit: "Follow-up",
    },
    {
      id: 3,
      content:
        "<p>Patient education provided regarding diabetes management. Discussed dietary modifications and importance of medication compliance. Patient demonstrates understanding.</p>",
      timestamp: "2024-06-26 11:45 AM",
      username: "Nurse Emily Davis",
      templateName: "Patient Education",
      reasonForVisit: "Education",
    },
  ]);

  const quillRef = useRef(null);

  // Templates for different note types
  const noteTemplates = {
    "": "",
    "Initial Assessment": `
      <h2>Initial Assessment</h2>
      <p><strong>Chief Complaint:</strong> </p>
      <p><strong>History of Present Illness:</strong> </p>
      <p><strong>Past Medical History:</strong> </p>
      <p><strong>Physical Examination:</strong> </p>
      <p><strong>Assessment and Plan:</strong> </p>
    `,
    "Follow-up": `
      <h2>Follow-up Visit</h2>
      <p><strong>Interval History:</strong> </p>
      <p><strong>Current Medications:</strong> </p>
      <p><strong>Review of Systems:</strong> </p>
      <p><strong>Physical Examination:</strong> </p>
      <p><strong>Assessment:</strong> </p>
      <p><strong>Plan:</strong> </p>
    `,
    "Discharge Summary": `
      <h2>Discharge Summary</h2>
      <p><strong>Admission Date:</strong> </p>
      <p><strong>Discharge Date:</strong> </p>
      <p><strong>Principal Diagnosis:</strong> </p>
      <p><strong>Hospital Course:</strong> </p>
      <p><strong>Discharge Medications:</strong> </p>
      <p><strong>Follow-up Instructions:</strong> </p>
    `,
    "Progress Note": `
      <h2>Progress Note</h2>
      <p><strong>Subjective:</strong> </p>
      <p><strong>Objective:</strong> </p>
      <p><strong>Assessment:</strong> </p>
      <p><strong>Plan:</strong> </p>
    `,
    Consultation: `
      <h2>Consultation Note</h2>
      <p><strong>Reason for Consultation:</strong> </p>
      <p><strong>History:</strong> </p>
      <p><strong>Examination:</strong> </p>
      <p><strong>Impression:</strong> </p>
      <p><strong>Recommendations:</strong> </p>
    `,
    "Patient Education": `
      <h2>Patient Education</h2>
      <p><strong>Topic Discussed:</strong> </p>
      <p><strong>Information Provided:</strong> </p>
      <p><strong>Patient Understanding:</strong> </p>
      <p><strong>Follow-up Required:</strong> </p>
    `,
  };

  // Reason for visit options
  const reasonForVisitOptions = [
    "Routine Check-up",
    "Follow-up",
    "New Complaint",
    "Chest Pain",
    "Shortness of Breath",
    "Headache",
    "Abdominal Pain",
    "Back Pain",
    "Diabetes Management",
    "Hypertension Follow-up",
    "Medication Review",
    "Lab Results Review",
    "Education",
    "Consultation",
    "Pre-operative Assessment",
    "Post-operative Follow-up",
  ];

  // Quill.js toolbar configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["clean"],
      ["link", "image"],
    ],
  };

  const quillFormats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  const handleTemplateChange = (templateKey) => {
    setSelectedTemplate(templateKey);
    setNoteContent(noteTemplates[templateKey] || "");
  };

  const handleNewNote = () => {
    setNoteContent("");
    setSelectedTemplate("");
    setReasonForVisit("");
  };

  const handleSaveNote = () => {
    if (!hasPermission('emr', 'create')) return;
    if (!noteContent.trim() || !reasonForVisit) {
      alert("Please fill in the reason for visit and note content.");
      return;
    }

    const newNote = {
      id: savedNotes.length + 1,
      content: noteContent,
      timestamp: new Date().toLocaleString(),
      username: "Dr. Current User", // Replace with actual user
      templateName: selectedTemplate || null,
      reasonForVisit: reasonForVisit,
    };

    setSavedNotes([newNote, ...savedNotes]);
    handleNewNote(); // Reset form
    alert("Note saved successfully!");
  };

  const handleDownloadNote = () => {
    // Simulate DOCX download
    const element = document.createElement("a");
    const content = noteContent.replace(/<[^>]*>/g, ""); // Strip HTML for demo
    const file = new Blob([content], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    element.href = URL.createObjectURL(file);
    element.download = `note_${patient?.name || "patient"}_${
      new Date().toISOString().split("T")[0]
    }.docx`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handlePrintNote = () => {
    // Simulate PDF print
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Patient Note - ${patient?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1, h2, h3 { color: #333; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Patient Note</h1>
            <p><strong>Patient:</strong> ${patient?.name}</p>
            <p><strong>Reason for Visit:</strong> ${reasonForVisit}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleString()}</p>
          </div>
          <div>${noteContent}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAttachFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      alert(
        `Selected ${files.length} file(s): ${files
          .map((f) => f.name)
          .join(", ")}`
      );
    };
    input.click();
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div className="h-full flex flex-col">
      {/* Add custom styles */}
      <style>{customStyles}</style>

      <div className="p-6 pb-4 flex-shrink-0">
        <h2 className="text-2xl font-bold">{t('Patient Notes')}</h2>
      </div>

      <div className="flex-1 px-6 pb-6 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 h-[calc(100vh-200px)]">
          {/* Left Side - Note Editor (3/4 width) */}
          <div className="lg:col-span-3 bg-gray-50 rounded-lg flex flex-col">
            <div className="p-4 pb-3 flex-shrink-0 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t('Create Note')}
              </h3>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                <CustomButton
                  onClick={handleNewNote}
                  label={t("New Note")}
                  icon={<FaPlus size={14} />}
                  buttonType="primary"
                  className="!h-[35px]"
                />
                <CustomButton
                  onClick={handleDownloadNote}
                  label={t("Download")}
                  icon={<FaDownload size={14} />}
                  buttonType="blue"
                  className="!h-[35px]"
                />
                <CustomButton
                  onClick={handleAttachFile}
                  label={t("Attach File")}
                  icon={<FaPaperclip size={14} />}
                  buttonType="primary"
                  className="!h-[35px]"
                />
                <CustomButton
                  onClick={handlePrintNote}
                  label={t("Print")}
                  icon={<FaPrint size={14} />}
                  buttonType="blue"
                  className="!h-[35px]"
                />
                <CustomButton
                  onClick={handleSaveNote}
                  label={t("Save Note")}
                  icon={<FaSave size={14} />}
                  buttonType="success"
                  className="!h-[35px]"
                />
              </div>

              {/* Reason for Visit */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Reason for Visit")}
                </label>
                <select
                  value={reasonForVisit}
                  onChange={(e) => setReasonForVisit(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t("Select reason for visit")}</option>
                  {reasonForVisitOptions.map((reason, index) => (
                    <option key={index} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              {/* Template Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Note Template")}
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t("Select template (optional)")}</option>
                  {Object.keys(noteTemplates)
                    .filter((key) => key !== "")
                    .map((templateKey) => (
                      <option key={templateKey} value={templateKey}>
                        {templateKey}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Quill Editor */}
            <div className="flex-1 p-4 overflow-hidden">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={noteContent}
                onChange={setNoteContent}
                modules={quillModules}
                formats={quillFormats}
                style={{ height: "calc(100% - 50px)" }}
                className="h-full"
              />
            </div>
          </div>

          {/* Right Side - Saved Notes (1/4 width) */}
          <div className="bg-white rounded-lg flex flex-col border border-gray-200">
            <div className="p-4 pb-3 flex-shrink-0 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("Saved Notes")} ({savedNotes.length})
              </h3>
            </div>
            <div className="overflow-y-auto p-4 h-[calc(100vh-250px)]">
              <div className="space-y-4">
                {savedNotes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200"
                  >
                    {/* Note Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FaUser size={12} />
                        <span className="font-medium">{note.username}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <FaClock size={10} />
                        <span>{note.timestamp}</span>
                      </div>
                    </div>

                    {/* Template Badge */}
                    {note.templateName && (
                      <div className="mb-2">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {t("Template")}: {note.templateName}
                        </span>
                      </div>
                    )}

                    {/* Reason for Visit */}
                    <div className="mb-2">
                      <span className="inline-block bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
                        {note.reasonForVisit}
                      </span>
                    </div>

                    {/* Note Content Preview */}
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {stripHtml(note.content).substring(0, 150)}
                      {stripHtml(note.content).length > 150 && "..."}
                    </div>

                    {/* Actions */}
                    <div className="mt-3 flex gap-2">
                      <CustomButton
                        onClick={() => {
                          setNoteContent(note.content);
                          setReasonForVisit(note.reasonForVisit);
                          setSelectedTemplate(note.templateName || "");
                        }}
                        label={t("Edit")}
                        buttonType="primary"
                        className="!h-[25px] !px-2 !text-xs"
                      />
                      <CustomButton
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to delete this note?"
                            )
                          ) {
                            setSavedNotes(
                              savedNotes.filter((n) => n.id !== note.id)
                            );
                          }
                        }}
                        label={t("Delete")}
                        buttonType="reset"
                        className="!h-[25px] !px-2 !text-xs"
                      />
                    </div>
                  </div>
                ))}

                {savedNotes.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <p>{t("No notes saved yet.")}</p>
                    <p className="text-sm mt-1">
                      {t("Create your first note to get started.")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesTab;
